/**
 * Modal (design-system.md §5 dialog elevation). Portals to `document.body`, traps Tab
 * focus inside, closes on Escape (and on overlay click unless `dismissOnOverlay={false}`
 * — destructive confirmations should pass false so a stray click never discards work),
 * moves focus in on open and restores it on close, and locks body scroll. Stacked
 * modals each own their own trap; the last-opened wins Escape via stopPropagation.
 */
import { X } from './icons.ts';
import {
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent,
  type ReactNode,
  type RefObject,
} from 'react';
import { createPortal } from 'react-dom';

import { Button } from './button.tsx';

const FOCUSABLE =
  'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  description?: ReactNode | undefined;
  children: ReactNode;
  footer?: ReactNode | undefined;
  size?: 'sm' | 'md' | 'lg' | 'xl' | undefined;
  /** `alertdialog` for confirmations that interrupt the user. */
  role?: 'dialog' | 'alertdialog' | undefined;
  dismissOnOverlay?: boolean | undefined;
  /** Ref of the element to focus on open (defaults to the first focusable). */
  initialFocusRef?: RefObject<HTMLElement> | undefined;
  /** Hide the header close (×) — e.g. when the footer already has Cancel. */
  hideClose?: boolean | undefined;
  /** Extra class on the dialog panel (e.g. a wide preview). */
  className?: string | undefined;
}

export function Modal({
  open,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'sm',
  role = 'dialog',
  dismissOnOverlay = true,
  initialFocusRef,
  hideClose = false,
  className,
}: ModalProps): JSX.Element | null {
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  const descId = `${titleId}-desc`;

  // Exit animation: keep the dialog mounted for one short reverse animation after `open`
  // flips to false. Skipped when the user prefers reduced motion (and in jsdom).
  const [closing, setClosing] = useState(false);
  const wasOpen = useRef(open);
  useEffect(() => {
    const motion =
      typeof window.matchMedia === 'function' &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (wasOpen.current && !open && motion) {
      setClosing(true);
      const timer = window.setTimeout(() => setClosing(false), 160);
      wasOpen.current = open;
      return () => window.clearTimeout(timer);
    }
    wasOpen.current = open;
    return undefined;
  }, [open]);

  // Focus management + body scroll lock for the lifetime of an open modal.
  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const panel = panelRef.current;
    const target =
      initialFocusRef?.current ?? panel?.querySelector<HTMLElement>(FOCUSABLE) ?? panel ?? null;
    target?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prevOverflow;
      previouslyFocused?.focus?.();
    };
  }, [open, initialFocusRef]);

  if (!open && !closing) return null;

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    if (event.key === 'Escape') {
      event.stopPropagation();
      onClose();
      return;
    }
    if (event.key !== 'Tab' || !panelRef.current) return;
    const nodes = Array.from(panelRef.current.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => !el.hidden && el.getAttribute('aria-hidden') !== 'true',
    );
    if (nodes.length === 0) {
      event.preventDefault();
      return;
    }
    const first = nodes[0]!;
    const last = nodes[nodes.length - 1]!;
    const active = document.activeElement;
    if (event.shiftKey && (active === first || active === panelRef.current)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && active === last) {
      event.preventDefault();
      first.focus();
    }
  }

  function onOverlayClick(event: MouseEvent<HTMLDivElement>): void {
    if (dismissOnOverlay && event.target === event.currentTarget) onClose();
  }

  return createPortal(
    <div
      className={['argus-modal-overlay', closing ? 'is-closing' : ''].filter(Boolean).join(' ')}
      onMouseDown={onOverlayClick}
      data-testid="modal-overlay"
    >
      <div
        ref={panelRef}
        role={role}
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descId : undefined}
        tabIndex={-1}
        className={['argus-modal', size !== 'sm' ? `argus-modal--${size}` : '', className ?? '']
          .filter(Boolean)
          .join(' ')}
        onKeyDown={onKeyDown}
      >
        <header className="argus-modal__header">
          <div className="grow">
            <h2 id={titleId} className="argus-modal__title">
              {title}
            </h2>
            {description ? (
              <p id={descId} className="argus-modal__description">
                {description}
              </p>
            ) : null}
          </div>
          {hideClose ? null : (
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label="Close"
              onClick={onClose}
              icon={<X size={16} aria-hidden="true" />}
            />
          )}
        </header>
        <div className="argus-modal__body">{children}</div>
        {footer ? <footer className="argus-modal__footer">{footer}</footer> : null}
      </div>
    </div>,
    document.body,
  );
}
